"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.EntityManager = void 0;
var tslib_1 = require("tslib");
var messages = tslib_1.__importStar(require("./message"));
var binding_1 = require("./binding");
var util_1 = require("./util");
var connector_1 = require("./connector");
var caching_1 = require("./caching");
var GeoPoint_1 = require("./GeoPoint");
var metamodel_1 = require("./metamodel");
var query_1 = require("./query");
var error_1 = require("./error");
var intersection_1 = require("./intersection");
var Message_1 = require("./connector/Message");
var MFAError_1 = require("./error/MFAError");
var message = tslib_1.__importStar(require("./message"));
var DB_PREFIX = '/db/';
var EntityManager = /** @class */ (function (_super) {
    tslib_1.__extends(EntityManager, _super);
    /**
     * @param entityManagerFactory The factory which of this entityManager instance
     */
    function EntityManager(entityManagerFactory) {
        var _this = _super.call(this) || this;
        /**
         * Constructor for a new List collection
         */
        _this.List = Array;
        /**
         * Constructor for a new Set collection
         */
        _this.Set = Set;
        /**
         * Constructor for a new Map collection
         */
        _this.Map = Map;
        /**
         * Constructor for a new GeoPoint
         */
        _this.GeoPoint = GeoPoint_1.GeoPoint;
        /**
         * Log messages can created by calling log directly as function, with a specific log level or with the helper
         * methods, which a members of the log method.
         *
         * Logs will be filtered by the client logger and the before they persisted. The default log level is
         * 'info' therefore all log messages below the given message aren't persisted.
         *
         * Examples:
         * <pre class="prettyprint">
         // default log level ist info
         db.log('test message %s', 'my string');
         // info: test message my string
      
         // pass a explicit log level as the first argument, one of ('trace', 'debug', 'info', 'warn', 'error')
         db.log('warn', 'test message %d', 123);
         // warn: test message 123
      
         // debug log level will not be persisted by default, since the default logging level is info
         db.log('debug', 'test message %j', {number: 123}, {});
         // debug: test message {"number":123}
         // data = {}
      
         // One additional json object can be provided, which will be persisted together with the log entry
         db.log('info', 'test message %s, %s', 'first', 'second', {number: 123});
         // info: test message first, second
         // data = {number: 123}
      
         //use the log level helper
         db.log.info('test message', 'first', 'second', {number: 123});
         // info: test message first second
         // data = {number: 123}
      
         //change the default log level to trace, i.e. all log levels will be persisted, note that the log level can be
         //additionally configured in the baqend
         db.log.level = 'trace';
      
         //trace will be persisted now
         db.log.trace('test message', 'first', 'second', {number: 123});
         // info: test message first second
         // data = {number: 123}
         * </pre>
         */
        _this.log = intersection_1.Logger.create(_this);
        /**
         * The connector used for requests
         */
        _this.connection = null;
        /**
         * All managed and cached entity instances
         * @type Map<String,Entity>
         * @private
         */
        _this.entities = {};
        _this.modules = new intersection_1.Modules(_this);
        /**
         * The current logged in user object
         */
        _this.me = null;
        /**
         * The current registered device object
         */
        _this.deviceMe = null;
        /**
         * Returns the tokenStorage which will be used to authorize all requests.
         */
        _this.tokenStorage = null; // is never null after em is ready
        /**
         * The bloom filter which contains staleness information of cached objects
         */
        _this.bloomFilter = null;
        /**
         * Set of object ids that were revalidated after the Bloom filter was loaded.
         */
        _this.cacheWhiteList = null;
        /**
         * Set of object ids that were updated but are not yet included in the bloom filter.
         * This set essentially implements revalidation by side effect which does not work in Chrome.
         */
        _this.cacheBlackList = null;
        /**
         * Bloom filter refresh interval in seconds.
         */
        _this.bloomFilterRefresh = 60;
        /**
         * Bloom filter refresh Promise
         */
        _this.bloomFilterLock = new util_1.Lockable();
        /**
         * A File factory for file objects.
         * The file factory can be called to create new instances for files.
         * The created instances implements the {@link File} interface
         */
        _this.File = null; // is never null after the em is ready
        /**
         * the shared connection data if this EntityManager shares the credentials with the EntityManagerFactory
         * @private
         */
        _this.connectData = null;
        _this.entityManagerFactory = entityManagerFactory;
        _this.metamodel = entityManagerFactory.metamodel;
        _this.code = entityManagerFactory.code;
        return _this;
    }
    Object.defineProperty(EntityManager.prototype, "isOpen", {
        /**
         * Determine whether the entity manager is open.
         * true until the entity manager has been closed
         */
        get: function () {
            return !!this.connection;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(EntityManager.prototype, "token", {
        /**
         * The authentication token if the user is logged in currently
         */
        get: function () {
            return this.tokenStorage.token;
        },
        /**
         * The authentication token if the user is logged in currently
         * @param value
         */
        set: function (value) {
            this.tokenStorage.update(value);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(EntityManager.prototype, "isCachingDisabled", {
        /**
         * Whether caching is disabled
         * @deprecated
         */
        get: function () {
            return !this.isCachingEnabled();
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Whether caching is enabled
     */
    EntityManager.prototype.isCachingEnabled = function () {
        return !!this.bloomFilter;
    };
    Object.defineProperty(EntityManager.prototype, "isDeviceRegistered", {
        /**
         * Returns true if the device token is already registered, otherwise false.
         */
        get: function () {
            return !!this.deviceMe;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Connects this entityManager, used for synchronous and asynchronous initialization
     * @param useSharedTokenStorage Indicates if the shared credentials should be used
     */
    EntityManager.prototype.connected = function (useSharedTokenStorage) {
        var _a;
        this.connection = this.entityManagerFactory.connection;
        this.bloomFilterRefresh = this.entityManagerFactory.staleness;
        this.tokenStorage = useSharedTokenStorage
            ? this.entityManagerFactory.tokenStorage : new intersection_1.TokenStorage(this.connection.origin);
        this.connectData = useSharedTokenStorage ? this.entityManagerFactory.connectData : null;
        this.File = binding_1.FileFactory.create(this);
        this._createObjectFactory(this.metamodel.embeddables);
        this._createObjectFactory(this.metamodel.entities);
        if (this.connectData) {
            if (this.connectData.device) {
                this._updateDevice(this.connectData.device);
            }
            if (this.connectData.user) {
                this._updateUser(this.connectData.user, true);
            }
        }
        var bloomFilter = (_a = this.entityManagerFactory.connectData) === null || _a === void 0 ? void 0 : _a.bloomFilter;
        if (this.bloomFilterRefresh > 0 && bloomFilter && typeof util_1.atob !== 'undefined' && !util_1.isNode) {
            this._updateBloomFilter(bloomFilter);
        }
    };
    /**
     * @param types
     * @return    * @private
     */
    EntityManager.prototype._createObjectFactory = function (types) {
        var values = Object.values(types);
        var _loop_1 = function (i) {
            var type = values[i];
            var name_1 = type.name;
            if (this_1[name_1]) {
                type.typeConstructor = this_1[name_1];
                Object.defineProperty(this_1, name_1, {
                    value: type.createObjectFactory(this_1),
                });
            }
            else {
                Object.defineProperty(this_1, name_1, {
                    get: function () {
                        Object.defineProperty(this, name_1, {
                            value: type.createObjectFactory(this),
                        });
                        return this[name_1];
                    },
                    set: function (typeConstructor) {
                        type.typeConstructor = typeConstructor;
                    },
                    configurable: true,
                });
            }
        };
        var this_1 = this;
        for (var i = 0; i < values.length; i += 1) {
            _loop_1(i);
        }
    };
    EntityManager.prototype.send = function (message, ignoreCredentialError) {
        var _this = this;
        if (ignoreCredentialError === void 0) { ignoreCredentialError = true; }
        if (!this.connection) {
            throw new Error('This EntityManager is not connected.');
        }
        var msg = message;
        msg.tokenStorage(this.tokenStorage);
        var result = this.connection.send(msg);
        if (!ignoreCredentialError) {
            result = result.catch(function (e) {
                if (e.status === connector_1.StatusCode.BAD_CREDENTIALS) {
                    _this._logout();
                }
                throw e;
            });
        }
        return result;
    };
    /**
     * Get an instance whose state may be lazily fetched
     *
     * If the requested instance does not exist in the database, the
     * EntityNotFoundError is thrown when the instance state is first accessed.
     * The application should not expect that the instance state will be available upon detachment,
     * unless it was accessed by the application while the entity manager was open.
     *
     * @param entityClass
     * @param key
     * @return
     */
    EntityManager.prototype.getReference = function (entityClass, key) {
        var id = null;
        var type;
        if (key) {
            var keyAsStr = key;
            type = this.metamodel.entity(entityClass);
            if (keyAsStr.indexOf(DB_PREFIX) === 0) {
                id = keyAsStr;
            }
            else {
                id = "".concat(type.ref, "/").concat(encodeURIComponent(keyAsStr));
            }
        }
        else if (typeof entityClass === 'string') {
            var keyIndex = entityClass.indexOf('/', DB_PREFIX.length); // skip /db/
            if (keyIndex !== -1) {
                id = entityClass;
            }
            type = this.metamodel.entity(keyIndex !== -1 ? entityClass.substring(0, keyIndex) : entityClass);
        }
        else {
            type = this.metamodel.entity(entityClass);
        }
        var entity = this.entities[id];
        if (!entity) {
            entity = type.create();
            var metadata = intersection_1.Metadata.get(entity);
            if (id) {
                metadata.id = id;
            }
            metadata.setUnavailable();
            this._attach(entity);
        }
        return entity;
    };
    /**
     * Creates an instance of {@link Builder<T>} for query creation and execution
     *
     * The query results are instances of the resultClass argument.
     *
     * @param resultClass - the type of the query result
     * @return A query builder to create one ore more queries for the specified class
     */
    EntityManager.prototype.createQueryBuilder = function (resultClass) {
        return new query_1.Builder(this, resultClass);
    };
    /**
     * Clear the persistence context, causing all managed entities to become detached
     *
     * Changes made to entities that have not been flushed to the database will not be persisted.
     *
     * @return
     */
    EntityManager.prototype.clear = function () {
        this.entities = {};
    };
    /**
     * Close an application-managed entity manager
     *
     * After the close method has been invoked, all methods on the EntityManager instance
     * and any Query and TypedQuery objects obtained from it will throw the IllegalStateError
     * except for transaction, and isOpen (which will return false). If this method
     * is called when the entity manager is associated with an active transaction,
     * the persistence context remains managed until the transaction completes.
     *
     * @return
     */
    EntityManager.prototype.close = function () {
        this.connection = null;
        return this.clear();
    };
    /**
     * Check if the instance is a managed entity instance belonging to the current persistence context
     *
     * @param entity - entity instance
     * @return boolean indicating if entity is in persistence context
     */
    EntityManager.prototype.contains = function (entity) {
        return !!entity && !!entity.id && this.entities[entity.id] === entity;
    };
    /**
     * Check if an object with the id from the given entity is already attached
     *
     * @param entity - entity instance
     * @return boolean indicating if entity with same id is attached
     */
    EntityManager.prototype.containsById = function (entity) {
        return !!(entity && entity.id && this.entities[entity.id]);
    };
    /**
     * Remove the given entity from the persistence context, causing a managed entity to become detached
     *
     * Unflushed changes made to the entity if any (including removal of the entity),
     * will not be synchronized to the database. Entities which previously referenced the detached entity will continue
     * to reference it.
     *
     * @param entity The entity instance to detach.
     * @return
     */
    EntityManager.prototype.detach = function (entity) {
        var _this = this;
        var state = intersection_1.Metadata.get(entity);
        return state.withLock(function () {
            _this.removeReference(entity);
            return Promise.resolve(entity);
        });
    };
    /**
     * Resolve the depth by loading the referenced objects of the given entity
     *
     * @param entity - entity instance
     * @param [options] The load options
     * @return
     */
    EntityManager.prototype.resolveDepth = function (entity, options) {
        var _this = this;
        if (!options || !options.depth) {
            return Promise.resolve(entity);
        }
        var resolved = options.resolved || [];
        var promises = [];
        var subOptions = tslib_1.__assign(tslib_1.__assign({}, options), { resolved: resolved, depth: options.depth === true ? true : options.depth - 1 });
        this.getSubEntities(entity, 1).forEach(function (subEntity) {
            if (subEntity !== null && resolved.indexOf(subEntity) === -1 && subEntity.id) {
                resolved.push(subEntity);
                promises.push(_this.load(subEntity.id, undefined, subOptions));
            }
        });
        return Promise.all(promises).then(function () { return entity; });
    };
    /**
     * Search for an entity of the specified oid
     *
     * If the entity instance is contained in the persistence context, it is returned from there.
     *
     * @param entityClass - entity class
     * @param oid - Object ID
     * @param [options] The load options.
     * @return the loaded entity or null
     */
    EntityManager.prototype.load = function (entityClass, oid, options) {
        var _this = this;
        var opt = options || {};
        var entity = this.getReference(entityClass, oid);
        var state = intersection_1.Metadata.get(entity);
        if (!opt.refresh && opt.local && state.isAvailable) {
            return this.resolveDepth(entity, opt);
        }
        var msg = new messages.GetObject(state.bucket, state.key);
        this.ensureCacheHeader(entity.id, msg, opt.refresh);
        return this.send(msg).then(function (response) {
            // refresh object if loaded older version from cache
            // chrome doesn't using cache when ifNoneMatch is set
            if (entity.version && entity.version > response.entity.version) {
                opt.refresh = true;
                return _this.load(entityClass, oid, opt);
            }
            _this.addToWhiteList(response.entity.id);
            if (response.status !== connector_1.StatusCode.NOT_MODIFIED) {
                state.type.fromJsonValue(state, response.entity, entity, { persisting: true });
            }
            return _this.resolveDepth(entity, opt);
        }, function (e) {
            if (e.status === connector_1.StatusCode.OBJECT_NOT_FOUND) {
                _this.removeReference(entity);
                state.setRemoved();
                return null;
            }
            throw e;
        });
    };
    /**
     * @param entity
     * @param options
     * @return
     */
    EntityManager.prototype.insert = function (entity, options) {
        var _this = this;
        var opt = options || {};
        var isNew;
        return this._save(entity, opt, function (state, json) {
            if (state.version) {
                throw new error_1.PersistentError('Existing objects can\'t be inserted.');
            }
            isNew = !state.id;
            return new messages.CreateObject(state.bucket, json);
        }).then(function (val) {
            if (isNew) {
                _this._attach(entity);
            }
            return val;
        });
    };
    /**
     * @param entity
     * @param options
     * @return
     */
    EntityManager.prototype.update = function (entity, options) {
        var opt = options || {};
        return this._save(entity, opt, function (state, json) {
            if (!state.version) {
                throw new error_1.PersistentError('New objects can\'t be inserted.');
            }
            if (opt.force) {
                var version = json.version, jsonWithoutVersion = tslib_1.__rest(json, ["version"]);
                return new messages.ReplaceObject(state.bucket, state.key, jsonWithoutVersion)
                    .ifMatch('*');
            }
            return new messages.ReplaceObject(state.bucket, state.key, json)
                .ifMatch("".concat(state.version));
        });
    };
    /**
     * @param entity
     * @param options The save options
     * @param withoutLock Set true to save the entity without locking
     * @return
     */
    EntityManager.prototype.save = function (entity, options, withoutLock) {
        if (withoutLock === void 0) { withoutLock = false; }
        var opt = options || {};
        var msgFactory = function (state, json) {
            if (opt.force) {
                if (!state.id) {
                    throw new error_1.PersistentError('New special objects can\'t be forcefully saved.');
                }
                var version = json.version, jsonWithoutVersion = tslib_1.__rest(json, ["version"]);
                return new messages.ReplaceObject(state.bucket, state.key, jsonWithoutVersion);
            }
            if (state.version) {
                return new messages.ReplaceObject(state.bucket, state.key, json)
                    .ifMatch(state.version);
            }
            return new messages.CreateObject(state.bucket, json);
        };
        return withoutLock ? this._locklessSave(entity, opt, msgFactory) : this._save(entity, opt, msgFactory);
    };
    /**
     * @param entity
     * @param cb pre-safe callback
     * @return
     */
    EntityManager.prototype.optimisticSave = function (entity, cb) {
        var _this = this;
        return intersection_1.Metadata.get(entity).withLock(function () { return _this._optimisticSave(entity, cb); });
    };
    /**
     * @param entity
     * @param cb pre-safe callback
     * @return
     * @private
     */
    EntityManager.prototype._optimisticSave = function (entity, cb) {
        var _this = this;
        var abort = false;
        var abortFn = function () {
            abort = true;
        };
        var promise = Promise.resolve(cb(entity, abortFn));
        if (abort) {
            return Promise.resolve(entity);
        }
        return promise.then(function () { return (_this.save(entity, {}, true)
            .catch(function (e) {
            if (e.status === 412) {
                return _this.refresh(entity, {})
                    .then(function () { return _this._optimisticSave(entity, cb); });
            }
            throw e;
        })); });
    };
    /**
     * Save the object state without locking
     * @param entity
     * @param options
     * @param msgFactory
     * @return
     * @private
     */
    EntityManager.prototype._locklessSave = function (entity, options, msgFactory) {
        var _this = this;
        this.attach(entity);
        var state = intersection_1.Metadata.get(entity);
        var refPromises;
        var json;
        if (state.isAvailable) {
            // getting json will check all collections changes, therefore we must do it before proofing the dirty state
            json = state.type.toJsonValue(state, entity, {
                persisting: true,
            });
        }
        if (state.isDirty) {
            if (!options.refresh) {
                state.setPersistent();
            }
            var sendPromise = this.send(msgFactory(state, json)).then(function (response) {
                if (state.id && state.id !== response.entity.id) {
                    _this.removeReference(entity);
                    state.id = response.entity.id;
                    _this._attach(entity);
                }
                state.type.fromJsonValue(state, response.entity, entity, {
                    persisting: options.refresh,
                    onlyMetadata: !options.refresh,
                });
                return entity;
            }, function (e) {
                if (e.status === connector_1.StatusCode.OBJECT_NOT_FOUND) {
                    _this.removeReference(entity);
                    state.setRemoved();
                    return null;
                }
                state.setDirty();
                throw e;
            });
            refPromises = [sendPromise];
        }
        else {
            refPromises = [Promise.resolve(entity)];
        }
        var subOptions = tslib_1.__assign({}, options);
        subOptions.depth = 0;
        this.getSubEntities(entity, options.depth).forEach(function (sub) {
            refPromises.push(_this._save(sub, subOptions, msgFactory));
        });
        return Promise.all(refPromises).then(function () { return entity; });
    };
    /**
     * Save and lock the object state
     * @param entity
     * @param options
     * @param msgFactory
     * @return
     * @private
     */
    EntityManager.prototype._save = function (entity, options, msgFactory) {
        var _this = this;
        this.ensureBloomFilterFreshness();
        var state = intersection_1.Metadata.get(entity);
        if (state.version) {
            this.addToBlackList(entity.id);
        }
        return state.withLock(function () { return _this._locklessSave(entity, options, msgFactory); });
    };
    /**
     * Returns all referenced sub entities for the given depth and root entity
     * @param entity
     * @param depth
     * @param [resolved]
     * @param initialEntity
     * @return
     */
    EntityManager.prototype.getSubEntities = function (entity, depth, resolved, initialEntity) {
        if (resolved === void 0) { resolved = []; }
        if (!depth) {
            return resolved;
        }
        var obj = initialEntity || entity;
        var state = intersection_1.Metadata.get(entity);
        var iter = state.type.references();
        for (var item = iter.next(); !item.done; item = iter.next()) {
            var value = item.value;
            var subEntities = this.getSubEntitiesByPath(entity, value.path);
            for (var i = 0, len = subEntities.length; i < len; i += 1) {
                var subEntity = subEntities[i];
                if (resolved.indexOf(subEntity) === -1 && subEntity !== obj) {
                    resolved.push(subEntity);
                    this.getSubEntities(subEntity, depth === true ? depth : depth - 1, resolved, obj);
                }
            }
        }
        return resolved;
    };
    /**
     * Returns all referenced one level sub entities for the given path
     * @param entity
     * @param path
     * @return
     */
    EntityManager.prototype.getSubEntitiesByPath = function (entity, path) {
        var _this = this;
        var subEntities = [entity];
        path.forEach(function (attributeName) {
            var tmpSubEntities = [];
            subEntities.forEach(function (subEntity) {
                var curEntity = subEntity[attributeName];
                if (!curEntity) {
                    return;
                }
                var attribute = _this.metamodel.managedType(subEntity.constructor).getAttribute(attributeName);
                if (attribute instanceof metamodel_1.PluralAttribute) {
                    var iter = curEntity.entries();
                    for (var item = iter.next(); !item.done; item = iter.next()) {
                        var entry = item.value;
                        tmpSubEntities.push(entry[1]);
                        if (attribute instanceof metamodel_1.MapAttribute && attribute.keyType.isEntity) {
                            tmpSubEntities.push(entry[0]);
                        }
                    }
                }
                else {
                    tmpSubEntities.push(curEntity);
                }
            });
            subEntities = tmpSubEntities;
        });
        return subEntities;
    };
    /**
     * Delete the entity instance.
     * @param entity
     * @param options The delete options
     * @return
     */
    EntityManager.prototype.delete = function (entity, options) {
        var _this = this;
        var opt = options || {};
        this.attach(entity);
        var state = intersection_1.Metadata.get(entity);
        return state.withLock(function () {
            if (!state.version && !opt.force) {
                throw new error_1.IllegalEntityError(entity);
            }
            var msg = new messages.DeleteObject(state.bucket, state.key);
            _this.addToBlackList(entity.id);
            if (!opt.force) {
                msg.ifMatch("".concat(state.version));
            }
            var refPromises = [_this.send(msg).then(function () {
                    _this.removeReference(entity);
                    state.setRemoved();
                    return entity;
                })];
            var subOptions = tslib_1.__assign({}, opt);
            subOptions.depth = 0;
            _this.getSubEntities(entity, opt.depth).forEach(function (sub) {
                refPromises.push(_this.delete(sub, subOptions));
            });
            return Promise.all(refPromises).then(function () { return entity; });
        });
    };
    /**
     * Synchronize the persistence context to the underlying database.
     *
     * @return
     */
    EntityManager.prototype.flush = function () {
        throw new Error('Not implemented.');
    };
    /**
     * Make an instance managed and persistent.
     * @param entity - entity instance
     * @return
     */
    EntityManager.prototype.persist = function (entity) {
        this.attach(entity);
    };
    /**
     * Refresh the state of the instance from the database, overwriting changes made to the entity, if any.
     * @param entity - entity instance
     * @param options The refresh options
     * @return
     */
    EntityManager.prototype.refresh = function (entity, options) {
        if (!entity.id) {
            // entity is not persisted so far
            return Promise.resolve(entity);
        }
        return this.load(entity.id, undefined, tslib_1.__assign(tslib_1.__assign({}, options), { refresh: true }));
    };
    /**
     * Attach the instance to this database context, if it is not already attached
     * @param entity The entity to attach
     * @return
     */
    EntityManager.prototype.attach = function (entity) {
        if (!this.contains(entity)) {
            var type = this.metamodel.entity(entity.constructor);
            if (!type) {
                throw new error_1.IllegalEntityError(entity);
            }
            if (this.containsById(entity)) {
                throw new error_1.EntityExistsError(entity);
            }
            this._attach(entity);
        }
    };
    EntityManager.prototype._attach = function (entity) {
        var metadata = intersection_1.Metadata.get(entity);
        if (metadata.isAttached) {
            if (metadata.db !== this) {
                throw new error_1.EntityExistsError(entity);
            }
        }
        else {
            metadata.db = this;
        }
        if (!metadata.id) {
            if (metadata.type.name !== 'User' && metadata.type.name !== 'Role' && metadata.type.name !== 'logs.AppLog') {
                metadata.id = "".concat(DB_PREFIX + metadata.type.name, "/").concat((0, util_1.uuid)());
            }
        }
        if (metadata.id) {
            this.entities[metadata.id] = entity;
        }
    };
    EntityManager.prototype.removeReference = function (entity) {
        var state = intersection_1.Metadata.get(entity);
        if (!state || !state.id) {
            throw new error_1.IllegalEntityError(entity);
        }
        delete this.entities[state.id];
    };
    EntityManager.prototype.register = function (user, password, loginOption) {
        var _this = this;
        var login = loginOption > binding_1.LoginOption.NO_LOGIN;
        if (this.me && login) {
            throw new error_1.PersistentError('User is already logged in.');
        }
        return this.withLock(function () {
            var msg = new messages.Register({ user: user, password: password, login: login });
            return _this._userRequest(msg, loginOption);
        });
    };
    EntityManager.prototype.login = function (username, password, loginOption) {
        var _this = this;
        if (this.me) {
            throw new error_1.PersistentError('User is already logged in.');
        }
        return this.withLock(function () {
            var msg = new messages.Login({ username: username, password: password });
            return _this._userRequest(msg, loginOption);
        });
    };
    EntityManager.prototype.logout = function () {
        var _this = this;
        return this.withLock(function () { return _this.send(new messages.Logout()).then(_this._logout.bind(_this)); });
    };
    /**
     * Starts the MFA initiate process - note you must be logged in, to start the mfa setup process
     *
     * @returns A promise that resolves to an object with the following properties:
     * - qrCode: A Base64 representation of the QR code for MFA setup.
     * - keyUri: The URI for the MFA secret key.
     * @example
     * const { qrCode, keyUri } = await db.initMFA();
     * const code = await setupMFADevice(qrCode, keyUri);
     * const user = await db.finishMFA(code);
     */
    EntityManager.prototype.initMFA = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            return tslib_1.__generator(this, function (_a) {
                return [2 /*return*/, this.send(new messages.MFAInitChallenge()).then(function (resp) {
                        return {
                            qrCode: resp.entity.qrCode,
                            keyUri: resp.entity.keyUri
                        };
                    })];
            });
        });
    };
    /**
     * Finishes the MFA (Multi-Factor Authentication) initiation process.
     *
     * @param code - The verification code for MFA.
     * @returns A promise that resolves with the user object of the logged-in user.
     */
    EntityManager.prototype.finishMFA = function (code) {
        var _this = this;
        return this.send(new messages.MFAInitFinish({ code: code })).then(function (resp) {
            return _this.User.me; // to be here user is already logged in;
        });
    };
    /**
     * Submit a verification code after a login
     *
     * @param code - A 6 digit verification code
     * @param token - An MFA token obtained during the login process
     * @return The logged-in user object
     */
    EntityManager.prototype.submitMFACode = function (code, token) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var loginType, msg;
            var _this = this;
            return tslib_1.__generator(this, function (_a) {
                loginType = this.tokenStorage.temporary ? binding_1.LoginOption.SESSION_LOGIN : binding_1.LoginOption.PERSIST_LOGIN;
                msg = new messages.MFAToken({
                    authToken: token,
                    code: code,
                    global: loginType === binding_1.LoginOption.PERSIST_LOGIN,
                });
                return [2 /*return*/, this.withLock(function () { return _this._userRequest(msg, loginType); })];
            });
        });
    };
    /**
     * Disables Multi-Factor Authentication for the currently logged in user.
     *
     * @throws {PersistentError} - Thrown when the user is not logged in.
     * @return A promise that resolves when Multi-Factor Authentication is successfully disabled.
     */
    EntityManager.prototype.disableMFA = function () {
        if (!this.User.me)
            throw new error_1.PersistentError('User not Logged in');
        return this.send(new messages.MFADelete());
    };
    /**
     * Returns the current MFA status of the user
     *
     * @returns A promise that resolves to the MFA status of the user.
     * Possible values are 'ENABLED' if MFA is enabled, 'DISABLED' if MFA is
     * disabled, or 'PENDING' if MFA status is pending.
     */
    EntityManager.prototype.getMFAStatus = function () {
        return this.send(new messages.MFAStatus()).then(function (resp) { return resp.entity; });
    };
    EntityManager.prototype.loginWithOAuth = function (provider, options) {
        if (!this.connection) {
            throw new Error('This EntityManager is not connected.');
        }
        if (this.me) {
            throw new error_1.PersistentError('User is already logged in.');
        }
        var opt = tslib_1.__assign({ title: "Login with ".concat(provider), timeout: 5 * 60 * 1000, state: {}, loginOption: true, oAuthVersion: 2, open: util_1.openWindow }, options);
        if (opt.deviceCode) {
            return this._loginOAuthDevice(provider.toLowerCase(), opt);
        }
        if (opt.oAuthVersion !== 1 && !opt.path && !opt.deviceCode) {
            throw new Error('No OAuth path is provided to start the OAuth flow.');
        }
        if (opt.redirect) {
            Object.assign(opt.state, { redirect: opt.redirect, loginOption: opt.loginOption });
        }
        var oAuthEndpoint = "".concat(this.connection.origin).concat(this.connection.basePath, "/db/User/OAuth/").concat(provider.toLowerCase());
        var url = opt.oAuthVersion === 1 ? oAuthEndpoint : (0, Message_1.appendQueryParams)(opt.path, {
            client_id: opt.clientId,
            scope: opt.scope,
            state: JSON.stringify(opt.state),
            redirect_uri: oAuthEndpoint,
        });
        var windowOptions = {
            title: opt.title,
            width: opt.width,
            height: opt.height,
        };
        if (opt.redirect) {
            // use oauth via redirect by opening the login in the same window
            return opt.open(url, tslib_1.__assign({ target: '_self' }, windowOptions)) || url;
        }
        var req = this._userRequest(new connector_1.OAuthMessage(), opt.loginOption);
        if (!opt.open(url, windowOptions)) {
            throw new Error('The OAuth flow with a Pop-Up can only be issued in browsers. Add a redirect URL to the options to return to your app via that redirect after the OAuth flow succeed.');
        }
        return new Promise(function (resolve, reject) {
            var timeout = setTimeout(function () {
                reject(new error_1.PersistentError('OAuth login timeout.'));
            }, opt.timeout);
            req.then(resolve, reject).then(function () {
                clearTimeout(timeout);
            });
        });
    };
    EntityManager.prototype._loginOAuthDevice = function (provider, opt) {
        var _this = this;
        return this._userRequest(new messages.OAuth2(provider, opt.deviceCode), opt.loginOption)
            .catch(function () { return new Promise(function (resolve) { return setTimeout(resolve, 5000); })
            .then(function () { return _this._loginOAuthDevice(provider, opt); }); });
    };
    EntityManager.prototype.renew = function (loginOption) {
        var _this = this;
        return this.withLock(function () {
            var msg = new messages.Me();
            return _this._userRequest(msg, loginOption);
        });
    };
    EntityManager.prototype.newPassword = function (username, password, newPassword) {
        var _this = this;
        return this.withLock(function () {
            var msg = new messages.NewPassword({ username: username, password: password, newPassword: newPassword });
            return _this.send(msg, true).then(function (response) { return _this._updateUser(response.entity); });
        });
    };
    EntityManager.prototype.newPasswordWithToken = function (token, newPassword, loginOption) {
        var _this = this;
        return this.withLock(function () { return (_this._userRequest(new messages.NewPassword({ token: token, newPassword: newPassword }), loginOption)); });
    };
    EntityManager.prototype.resetPassword = function (username) {
        return this.send(new messages.ResetPassword({ username: username }));
    };
    EntityManager.prototype.changeUsername = function (username, newUsername, password) {
        return this.send(new messages.ChangeUsername({ username: username, newUsername: newUsername, password: password }));
    };
    EntityManager.prototype._updateUser = function (obj, updateMe) {
        if (updateMe === void 0) { updateMe = false; }
        var user = this.getReference(obj.id);
        var metadata = intersection_1.Metadata.get(user);
        metadata.type.fromJsonValue(metadata, obj, user, { persisting: true });
        if (updateMe) {
            this.me = user;
            if (this.connectData) {
                this.connectData.user = obj;
            }
        }
        return user;
    };
    EntityManager.prototype._logout = function () {
        this.me = null;
        this.token = null;
        if (this.connectData) {
            delete this.connectData.user;
        }
    };
    EntityManager.prototype._userRequest = function (msg, loginOption) {
        var _this = this;
        var opt = loginOption === undefined ? true : loginOption;
        var login = opt > binding_1.LoginOption.NO_LOGIN;
        if (login) {
            this.tokenStorage.temporary = opt < binding_1.LoginOption.PERSIST_LOGIN;
        }
        return this.send(msg, !login)
            .then(function (response) {
            return response.entity ? _this._updateUser(response.entity, login) : null;
        }, function (e) {
            if (e.status === connector_1.StatusCode.OBJECT_NOT_FOUND) {
                if (login) {
                    _this._logout();
                }
                return null;
            }
            if (e.status === connector_1.StatusCode.FORBIDDEN) {
                var data = e.data;
                throw new MFAError_1.MFAError(data['baqend-mfa-auth-token']); // If MFA is required: throw an error containing the auth token
            }
            throw e;
        });
    };
    /**
     * @param deviceType The OS of the device (IOS/Android)
     * @param subscription WebPush subscription
     * @param device
     * @return
     */
    EntityManager.prototype.registerDevice = function (deviceType, subscription, device) {
        var _this = this;
        var msg = new messages.DeviceRegister({ devicetype: deviceType, subscription: subscription, device: device });
        msg.withCredentials = true;
        return this.send(msg)
            .then(function (response) { return _this._updateDevice(response.entity); });
    };
    EntityManager.prototype._updateDevice = function (obj) {
        var device = this.getReference(obj.id);
        var metadata = intersection_1.Metadata.get(device);
        metadata.type.fromJsonValue(metadata, obj, device, { persisting: true });
        this.deviceMe = device;
        if (this.connectData) {
            this.connectData.device = obj;
        }
        return device;
    };
    EntityManager.prototype.checkDeviceRegistration = function () {
        return this.send(new messages.DeviceRegistered())
            .then(function () { return true; }, function (e) {
            if (e.status === connector_1.StatusCode.OBJECT_NOT_FOUND) {
                return false;
            }
            throw e;
        });
    };
    EntityManager.prototype.pushDevice = function (pushMessage) {
        return this.send(new messages.DevicePush(pushMessage.toJSON()));
    };
    /**
     * The given entity will be checked by the validation code of the entity type.
     *
     * @param entity
     * @return result
     */
    EntityManager.prototype.validate = function (entity) {
        var type = intersection_1.Metadata.get(entity).type;
        var result = new intersection_1.ValidationResult();
        var iter = type.attributes();
        for (var item = iter.next(); !item.done; item = iter.next()) {
            var validate = new intersection_1.Validator(item.value.name, entity);
            result.fields[validate.key] = validate;
        }
        var validationCode = type.validationCode;
        if (validationCode) {
            validationCode(result.fields);
        }
        return result;
    };
    /**
     * Adds the given object id to the cacheWhiteList if needed.
     * @param objectId The id to add.
     * @return
     */
    EntityManager.prototype.addToWhiteList = function (objectId) {
        if (this.isCachingEnabled()) {
            if (this.bloomFilter.contains(objectId) || this.cacheBlackList.has(objectId)) {
                this.cacheWhiteList.add(objectId);
            }
        }
    };
    /**
     * Adds the given object id to the cacheBlackList if needed.
     * @param objectId The id to add.
     * @return
     */
    EntityManager.prototype.addToBlackList = function (objectId) {
        if (this.isCachingEnabled() && objectId) {
            if (!this.bloomFilter.contains(objectId)) {
                this.cacheBlackList.add(objectId);
            }
            this.cacheWhiteList.delete(objectId);
        }
    };
    EntityManager.prototype.refreshBloomFilter = function () {
        var _this = this;
        if (!this.isCachingEnabled()) {
            return Promise.resolve(null);
        }
        var msg = new messages.GetBloomFilter();
        msg.noCache();
        return this.send(msg).then(function (response) {
            _this._updateBloomFilter(response.entity);
            return _this.bloomFilter;
        });
    };
    EntityManager.prototype._updateBloomFilter = function (bloomFilter) {
        this.bloomFilter = new caching_1.BloomFilter(bloomFilter);
        this.cacheWhiteList = new Set();
        this.cacheBlackList = new Set();
    };
    /**
     * Checks the freshness of the bloom filter and does a reload if necessary
     */
    EntityManager.prototype.ensureBloomFilterFreshness = function () {
        var _this = this;
        if (!this.isCachingEnabled()) {
            return;
        }
        var now = new Date().getTime();
        var refreshRate = this.bloomFilterRefresh * 1000;
        if (this.bloomFilterLock.isReady && now - this.bloomFilter.creation > refreshRate) {
            this.bloomFilterLock.withLock(function () { return _this.refreshBloomFilter(); });
        }
    };
    /**
     * Checks for a given id, if revalidation is required, the resource is stale or caching was disabled
     * @param id The object id to check
     * @return Indicates if the resource must be revalidated
     */
    EntityManager.prototype.mustRevalidate = function (id) {
        if (util_1.isNode) {
            return false;
        }
        this.ensureBloomFilterFreshness();
        if (!this.isCachingEnabled() || !this.bloomFilterLock.isReady) {
            return true;
        }
        if (Message_1.CACHE_REPLACEMENT_SUPPORTED && this.cacheWhiteList.has(id)) {
            return false;
        }
        return this.cacheBlackList.has(id) || this.bloomFilter.contains(id);
    };
    /**
     * @param id To check the bloom filter
     * @param message To attach the headers
     * @param refresh To force the reload headers
     * @return
     */
    EntityManager.prototype.ensureCacheHeader = function (id, message, refresh) {
        var noCache = refresh || !id || this.mustRevalidate(id);
        if (noCache) {
            message.noCache();
        }
    };
    /**
     * Creates a absolute url for the given relative one
     * @param relativePath the relative url
     * @param authorize indicates if authorization credentials should be generated and be attached to the url
     * @return a absolute url which is optionally signed with a resource token which authenticates the currently
     * logged in user
     */
    EntityManager.prototype.createURL = function (relativePath, authorize) {
        var _this = this;
        var connection = this.connection;
        if (!connection) {
            throw new Error('This EntityManager is not connected.');
        }
        return this.tokenStorage.signPath(connection.basePath + relativePath, authorize)
            .then(function (path) {
            var url = connection.origin + path;
            if (_this.mustRevalidate(relativePath)) {
                url += "".concat(authorize ? '&' : '?', "BCB");
            }
            return url;
        });
    };
    /**
     * Requests a perpetual token for the given user
     *
     * Only users with the admin role are allowed to request an API token.
     *
     * @param entityClass
     * @param user The user object or id of the user object
     * @return
     */
    EntityManager.prototype.requestAPIToken = function (entityClass, user) {
        var userObj = this._getUserReference(entityClass, user);
        var msg = new messages.UserToken(userObj.key);
        return this.send(msg).then(function (resp) { return resp.entity; });
    };
    /**
     * Revoke all created tokens for the given user
     *
     * This method will revoke all previously issued tokens and the user must login again.
     *
     * @param entityClass
     * @param user The user object or id of the user object
     */
    EntityManager.prototype.revokeAllTokens = function (entityClass, user) {
        var userObj = this._getUserReference(entityClass, user);
        var msg = new messages.RevokeUserToken(userObj.key);
        return this.send(msg);
    };
    EntityManager.prototype.executeQuery = function (resultClass, query, triggeredBy) {
        var _a;
        var type = resultClass ? this.metamodel.entity(resultClass) : null;
        var backendType = type === null || type === void 0 ? void 0 : type.getMetadata("backendType");
        if (!type || !backendType || backendType !== "analytics") {
            throw new Error("Only analytics entities can execute queries");
        }
        var uriSize = (((_a = this.connection) === null || _a === void 0 ? void 0 : _a.host.length) || 0) + query.length;
        var msg;
        if (uriSize > query_1.Query.MAX_URI_SIZE) {
            msg = new messages.AdhocQueryPOST(type.name, undefined, undefined, undefined, triggeredBy, query)
                .entity(query, 'text');
        }
        else {
            msg = new message.AdhocQuery(type.name, query, undefined, undefined, undefined, undefined, undefined, triggeredBy);
        }
        return this.send(msg).then(function (response) { return response.entity; });
    };
    EntityManager.prototype._getUserReference = function (entityClass, user) {
        if (typeof user === 'string') {
            return this.getReference(entityClass, user);
        }
        return user;
    };
    tslib_1.__decorate([
        (0, util_1.deprecated)('EntityManager.isCachingEnabled()')
    ], EntityManager.prototype, "isCachingDisabled", null);
    return EntityManager;
}(util_1.Lockable));
exports.EntityManager = EntityManager;
//# sourceMappingURL=data:application/json;base64,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